import Amplify, { Auth } from 'aws-amplify';
import { CognitoUser } from '@aws-amplify/auth';
import { getPartner, getPartnerCode, PRODUCT_CODES, ProductCode } from 'packages/smart-home-support/partner';
import { User, UserAttributes, ExtraLoginData } from '../models';
import config from '../config';
import {
  getResponseError,
  AlreadyConfirmedError,
  CodeMismatchError,
  InvalidParameterError,
  InvalidPasswordError,
  NotAuthorizedError,
  PasswordResetRequiredError,
  TooManyAttemptsError,
  UsernameExistsError,
  UserNotConfirmedError,
  UserNotFoundError,
  UnknownError,
} from './errors';
import { CustomAttributes } from './constants';
import smartHomeApiClient from '../smartHomeApiClient';

let isAmplifyConfigured = false;
if (!isAmplifyConfigured) {
  Amplify.configure({
    Auth: {
      region: 'us-east-1',
      userPoolId: config.COGNITO_USER_POOL_ID,
      userPoolWebClientId: config.COGNITO_CLIENT_ID,
      cookieStorage: globals.NODE_ENV === 'production' ? { domain: window.location.hostname } : undefined,
    },
    Analytics: { disabled: true },
  });
  isAmplifyConfigured = true;
}

const getCognitoUser = async () => {
  try {
    return await Auth.currentAuthenticatedUser();
  } catch (err) {
    return null;
  }
};

enum CustomJWTClaimKeys {
  UserProducts = 'asurion:sh_web:user_products',
}

type AmplifyCognitoUser<TAttributes extends Record<string, any>, TClaims extends Record<string, any>> = {
  attributes: TAttributes;
  signInUserSession: {
    idToken: {
      payload: TClaims;
    };
  };
};

type CognitoAttributes = {
  phone_number: string;
  phone_number_verified: boolean;
};

type JWTClaims = {
  [CustomJWTClaimKeys.UserProducts]: string; // comma-separated list containing ProductCodes
};

const mapCognitoUser = (user: AmplifyCognitoUser<CognitoAttributes, JWTClaims>): User => {
  const claims = user.signInUserSession.idToken.payload || {};
  const productCodes = (claims[CustomJWTClaimKeys.UserProducts] || '').split(',');

  const parseUserAttributes = (key: string) => (
    user.attributes[key]
      ? JSON.parse(user.attributes[key])
      : undefined
  );

  let productCode: ProductCode | undefined = productCodes.find((code: ProductCode) =>
    PRODUCT_CODES.includes(code)
  ) as ProductCode;

  if (!productCode) {
    const { availableProductCodes } = getPartner();
    const fallbackProductCode = availableProductCodes && availableProductCodes[0];
    productCode = fallbackProductCode;
  }

  return {
    mdn: user.attributes.phone_number,
    isMdnVerified: user.attributes.phone_number_verified,
    home: {
      devices: parseUserAttributes(CustomAttributes.HomeDevices)
    },
    productCode,
    testGroups: parseUserAttributes(CustomAttributes.TestGroups),
  };
};

const prevalidatePassword = (password: string): void => {
  if (password.length < 6) {
    throw new InvalidPasswordError('Password must be at least 6 characters.');
  }
  if (password.length > 256) {
    throw new InvalidPasswordError('Password must not be longer than 256 characters.');
  }
  if (!password.match(/^[\S]+.*[\S]+$/)) {
    throw new InvalidPasswordError('Password must not begin or end with whitespace characters.');
  }
};

let cognitoUser: CognitoUser | undefined = undefined;

export default {
  /**
   * @throws TooManyAttemptsError
   * @throws UnknownError
   *         pretty much all of the standard Cognito errors aren't relevant when it
   *         comes to passwordless, so they would suggest an issue with our
   *         implementation or a service outage.
   */
  async loginPasswordless(username: string, extraLoginData?: ExtraLoginData): Promise<void> {
    // TODO: throw TooManyAttemptsError
    const partnerCode = config.AUTH_API_PARTNER_CODE || getPartnerCode();
    try {
      await smartHomeApiClient.createUserIfNotPresent({ mdn: username, partner: partnerCode, extraLoginData });
      cognitoUser = await Auth.signIn(username);
    } catch (err) {
      throw getResponseError(err, {});
    }
  },

  /**
   * @throws CodeMismatchError
   * @throws CodeExpiredError
   * @throws TooManyAttemptsError
   */
  async confirmLoginPasswordless(code: string): Promise<User> {
    // TODO: throw CodeExpiredError
    try {
      const response = await Auth.sendCustomChallengeAnswer(cognitoUser, code);
      if (response.challengeName === 'CUSTOM_CHALLENGE') {
        throw new CodeMismatchError();
      }
      await Auth.currentSession(); // throws error if not authenticated
      return mapCognitoUser(response);
    } catch (err) {
      throw getResponseError(err, {
        NotAuthorizedException: new TooManyAttemptsError(
          "You've exceeded the maximum number of attempts. Please go back and try again."
        ),
      });
    }
  },

  /**
   * @throws see loginPasswordless
=   */
  async resendConfirmationPasswordless(username: string): Promise<void> {
    await this.loginPasswordless(username);
  },

  /**
   * @throws InvalidPasswordError
   * @throws InvalidParameterError
   *         when any input other than password is invalid.
   * @throws UsernameExistsError
   *         when there's already an account for that username
   * @throws UnknownError
   */
  async signUp(username: string, password: string, attributes?: UserAttributes): Promise<void> {
    prevalidatePassword(password);
    try {
      await Auth.signUp({ username, password, attributes });
    } catch (err) {
      throw getResponseError(err, {
        UsernameExistsException: new UsernameExistsError('An account with the given username already exists.'),
        InvalidParameterException: new InvalidParameterError(),
      });
    }
  },

  /**
   * @throws CodeMismatchError
   * @throws AlreadyConfirmedError
   * @throws UserNotFoundError
   * @throws UnknownError
   */
  async confirmSignup(username: string, code: string): Promise<void> {
    try {
      await Auth.confirmSignUp(username, code);
    } catch (err) {
      throw getResponseError(err, {
        CodeMismatchException: new CodeMismatchError(),
        NotAuthorizedException: (err) => {
          return err.message.match(/Current status is CONFIRMED/)
            ? new AlreadyConfirmedError()
            : new NotAuthorizedError();
        },
        UserNotFoundException: new UserNotFoundError(),
      });
    }
  },

  /**
   * @throws UserNotFoundError
   * @throws AlreadyConfirmedError
   * @throws UnknownError
   */
  async resendConfirmation(username: string): Promise<void> {
    try {
      await Auth.resendSignUp(username);
    } catch (err) {
      throw getResponseError(err, {
        UserNotFoundException: new UserNotFoundError(),
        InvalidParameterException: (err) =>
          err.message.match(/User is already confirmed/) ? new AlreadyConfirmedError() : new InvalidParameterError(),
      });
    }
  },

  /**
   * @throws NotAuthorizedError
   *         when either the user does not exist, or the password is incorrect.
   * @throws UserNotConfirmedError
   *         when the user has signed up, but not yet confirmed.
   * @throws PasswordResetRequiredError
   *         when a password reset is required for some reason.
   * @throws UnknownError
   */
  async signIn(username: string, password: string): Promise<User> {
    prevalidatePassword(password);
    try {
      return mapCognitoUser(await Auth.signIn(username, password));
    } catch (err) {
      throw getResponseError(err, {
        NotAuthorizedException: new NotAuthorizedError(),
        UserNotFoundException: new NotAuthorizedError(),
        UserNotConfirmedException: new UserNotConfirmedError('Please confirm your account before signing in.'),
        PasswordResetRequiredException: new PasswordResetRequiredError('Please reset your password before signing in.'),
      });
    }
  },

  /**
   * @throws InvalidPasswordError
   * @throws UserNotFoundError
   *         when no user is logged in
   * @throws NotAuthorizedError
   *         when the old password is incorrect
   * @throws UnknownError
   */
  async changePassword(oldPassword: string, newPassword: string): Promise<void> {
    prevalidatePassword(oldPassword);
    prevalidatePassword(newPassword);

    const user = await getCognitoUser();
    if (!user) {
      throw new UserNotFoundError('No user logged in.');
    }

    try {
      await Auth.changePassword(user, oldPassword, newPassword);
    } catch (err) {
      throw getResponseError(err, {
        InvalidParameterException: (err) =>
          err.message.match(/: Value at 'previousPassword'/)
            ? new NotAuthorizedError('Incorrect password.')
            : new InvalidParameterError(),
      });
    }
  },

  /**
   * @throws InvalidParameterError
   *         when the username is wrong
   * @throws UserNotFoundError
   *         when no user exists for the give username
   * @throws UnknownError
   */
  async forgotPassword(username: string): Promise<void> {
    try {
      await Auth.forgotPassword(username);
    } catch (err) {
      throw getResponseError(err, {
        InvalidParameterException: new InvalidParameterError(),
        UserNotFoundException: new UserNotFoundError(),
      });
    }
  },

  /**
   * @throws InvalidPasswordError
   * @throws UnknownError
   */
  async confirmForgotPassword(username: string, code: string, newPassword: string) {
    prevalidatePassword(newPassword);
    try {
      await Auth.forgotPasswordSubmit(username, code, newPassword);
    } catch (err) {
      throw getResponseError(err, {
        InvalidParameterException: new InvalidParameterError(),
        UserNotFoundException: new UserNotFoundError(),
      });
    }
  },

  /**
   * @throws UnknownError
   */
  async signOut(): Promise<void> {
    try {
      await Auth.signOut();
    } catch (err) {
      throw new UnknownError();
    }
  },

  /**
   * @throws UnknownError
   */
  async getUser(): Promise<User | null> {
    try {
      const user = await getCognitoUser();
      return user ? mapCognitoUser(user) : null;
    } catch (err) {
      throw new UnknownError();
    }
  },

  /**
   * @throws UnknownError
   */
  async updateUserAttributes(userAttributes: UserAttributes): Promise<void> {
    const user = await getCognitoUser();
    if (user) {
      try {
        await Auth.updateUserAttributes(user, userAttributes);
      } catch (error) {
        // How do we want to track these?
        console.error(`Error Updating User Attributes: ${error}`);
        throw getResponseError(error, {});
      }
    }
  },
};



// WEBPACK FOOTER //
// ./packages/smart-home-support/api/authClient/authClient.ts